/* eslint-disable max-len */
// src/components/trends/TrendsPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useToast } from '../shared/contexts/ToastContext';
import {
    FiRefreshCw, FiTrendingUp, FiChevronDown, FiChevronUp, FiZap,
    FiBookmark, FiArrowRight, FiX
} from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';

// --- Constants ---
const CATEGORY_CONFIG = {
    breaking: { label: 'Breaking', className: 'bg-red-400/15 text-red-300 border-red-400/25' },
    rising: { label: 'Rising', className: 'bg-amber-400/15 text-amber-300 border-amber-400/25' },
    seasonal: { label: 'Seasonal', className: 'bg-green-400/15 text-green-300 border-green-400/25' },
    evergreen: { label: 'Evergreen', className: 'bg-slate-blue/15 text-slate-blue border-slate-blue/25' },
};

const FORMAT_LABELS = {
    standard: 'Post',
    carousel: 'Carousel',
    reel: 'Reel',
    story: 'Story',
    poll: 'Poll',
    article: 'Article',
};

const CATEGORY_FILTERS = ['all', 'breaking', 'rising', 'seasonal', 'evergreen'];

// --- Cloud Function References ---
const generateNicheTrendsFn = httpsCallable(functions, 'generateNicheTrends');
const saveTrendFn = httpsCallable(functions, 'saveTrend');
const removeSavedTrendFn = httpsCallable(functions, 'removeSavedTrend');
const getSavedTrendsFn = httpsCallable(functions, 'getSavedTrends');

// --- Sub-components ---

function TrendCard({ trend, platform, onSave, onRemove, isSaved, savedId, onCreateFrom }) {
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const category = CATEGORY_CONFIG[trend.category] || CATEGORY_CONFIG.rising;

    const handleSaveToggle = async () => {
        setSaving(true);
        try {
            if (isSaved && savedId) {
                await onRemove(savedId);
            } else {
                await onSave(trend, platform);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-5 bg-warm-gray/20 border border-white/5 rounded-2xl hover:border-slate-blue/30 transition-all group">
            <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-sm font-bold text-cream leading-snug flex-1">{trend.title}</h4>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${category.className}`}>
                    {category.label}
                </span>
            </div>
            <p className="text-xs text-cream/50 leading-relaxed mb-2">{trend.description}</p>
            {trend.relevance && (
                <p className="text-xs text-slate-blue/80 italic mb-3">{trend.relevance}</p>
            )}

            {/* Content Angles */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs font-bold text-cream/40 hover:text-cream/60 flex items-center gap-1 mb-2 transition-colors"
            >
                {expanded ? <FiChevronUp className="h-3 w-3" /> : <FiChevronDown className="h-3 w-3" />}
                {trend.contentAngles?.length || 0} content ideas
            </button>

            {expanded && trend.contentAngles && (
                <div className="space-y-1.5 animate-fade-in mb-3">
                    {trend.contentAngles.map((angle, i) => (
                        <div key={i} className="flex items-center gap-2 p-2.5 bg-black/20 border border-white/5 rounded-xl text-xs text-cream/70">
                            <FiZap className="h-3 w-3 text-cream/20 shrink-0" />
                            <span className="flex-1">{typeof angle === 'object' ? angle.angle : angle}</span>
                            {typeof angle === 'object' && angle.format && (
                                <span className="text-[9px] font-bold text-cream/30 uppercase tracking-wider px-1.5 py-0.5 bg-white/5 rounded">
                                    {FORMAT_LABELS[angle.format] || angle.format}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <button
                    onClick={handleSaveToggle}
                    disabled={saving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        isSaved
                            ? 'bg-slate-blue/20 text-slate-blue border border-slate-blue/30'
                            : 'text-cream/40 hover:text-cream hover:bg-white/5 border border-transparent'
                    }`}
                >
                    <FiBookmark className={`h-3 w-3 ${isSaved ? 'fill-current' : ''}`} />
                    {saving ? '...' : isSaved ? 'Saved' : 'Save'}
                </button>
                <button
                    onClick={() => onCreateFrom(trend, platform)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-cream/40 hover:text-slate-blue hover:bg-slate-blue/10 transition-all ml-auto"
                >
                    Create from this <FiArrowRight className="h-3 w-3" />
                </button>
            </div>
        </div>
    );
}

function SkeletonCards() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="p-5 bg-warm-gray/20 border border-white/5 rounded-2xl space-y-3">
                    <div className="flex justify-between">
                        <div className="h-4 w-3/4 bg-white/10 rounded" />
                        <div className="h-4 w-16 bg-white/10 rounded-full" />
                    </div>
                    <div className="h-3 w-full bg-white/5 rounded" />
                    <div className="h-3 w-2/3 bg-white/5 rounded" />
                    <div className="h-3 w-1/2 bg-white/5 rounded" />
                </div>
            ))}
        </div>
    );
}

// --- Main Component ---

export default function TrendsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedProjectId = searchParams.get('project') || null;
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { userId } = useAuthUser();
    const isMountedRef = useRef(true);

    // State
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [projectData, setProjectData] = useState(null);

    const [platform, setPlatform] = useState('meta');
    const [categoryFilter, setCategoryFilter] = useState('all');

    const [trends, setTrends] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cached, setCached] = useState(false);

    const [savedTrends, setSavedTrends] = useState([]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Fetch user's projects
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;

        const fetchProjects = async () => {
            try {
                const q = query(collection(db, 'projects'), where('userId', '==', userId));
                const snap = await getDocs(q);
                if (cancelled) return;
                const list = snap.docs.map((d) => ({
                    id: d.id,
                    name: d.data().projectName || 'Untitled',
                    hasLinkedIn: !!d.data().associatedLinkedInId,
                }));
                list.sort((a, b) => a.name.localeCompare(b.name));
                setProjects(list);
            } catch {
                // Silently fail
            } finally {
                if (!cancelled) setProjectsLoading(false);
            }
        };

        fetchProjects();
        return () => { cancelled = true; };
    }, [userId]);

    // Fetch project data when project selected
    useEffect(() => {
        if (!selectedProjectId) { setProjectData(null); return; }
        let cancelled = false;
        const fetchProject = async () => {
            try {
                const snap = await getDoc(doc(db, 'projects', selectedProjectId));
                if (cancelled) return;
                if (snap.exists()) setProjectData({ id: snap.id, ...snap.data() });
            } catch {
                // Silently fail
            }
        };
        fetchProject();
        return () => { cancelled = true; };
    }, [selectedProjectId]);

    // Fetch trends when project or platform changes
    const fetchTrends = useCallback(async (forceRefresh = false) => {
        if (!selectedProjectId) return;
        setLoading(true);
        try {
            const result = await generateNicheTrendsFn({ projectId: selectedProjectId, platform, forceRefresh });
            if (!isMountedRef.current) return;
            if (result.data.success && result.data.trends) {
                setTrends(result.data.trends);
                setCached(result.data.cached || false);
            }
        } catch {
            if (isMountedRef.current) addToast('Failed to load trends. Please try again.', 'error');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [selectedProjectId, platform, addToast]);

    useEffect(() => {
        if (selectedProjectId) {
            fetchTrends(false);
        } else {
            setTrends([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId, platform]);

    // Fetch saved trends
    const fetchSavedTrends = useCallback(async () => {
        if (!selectedProjectId) return;
        try {
            const result = await getSavedTrendsFn({ projectId: selectedProjectId });
            if (isMountedRef.current && result.data.success) {
                setSavedTrends(result.data.trends || []);
            }
        } catch {
            // Silently fail
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (selectedProjectId) fetchSavedTrends();
        else setSavedTrends([]);
    }, [selectedProjectId, fetchSavedTrends]);

    // --- Handlers ---

    const handleSaveTrend = useCallback(async (trend, trendPlatform) => {
        try {
            const result = await saveTrendFn({ projectId: selectedProjectId, trend: { ...trend, platform: trendPlatform } });
            if (result.data.duplicate) {
                addToast('This trend is already saved.', 'info');
                return;
            }
            if (result.data.success) {
                addToast('Trend saved!', 'success');
                fetchSavedTrends();
            }
        } catch {
            addToast('Failed to save trend.', 'error');
        }
    }, [selectedProjectId, addToast, fetchSavedTrends]);

    const handleRemoveSavedTrend = useCallback(async (trendId) => {
        try {
            const result = await removeSavedTrendFn({ projectId: selectedProjectId, trendId });
            if (result.data.success) {
                addToast('Trend removed.', 'success');
                setSavedTrends((prev) => prev.filter((t) => t.id !== trendId));
            }
        } catch {
            addToast('Failed to remove trend.', 'error');
        }
    }, [selectedProjectId, addToast]);

    const handleCreateFrom = useCallback((trend, trendPlatform) => {
        if (!selectedProjectId) return;
        const bestAngle = trend.contentAngles?.[0];
        navigate(`/project/${selectedProjectId}/content-hub`, {
            state: {
                selectedTrend: {
                    title: trend.title,
                    angle: typeof bestAngle === 'object' ? bestAngle.angle : bestAngle || trend.title,
                    category: trend.category,
                    platform: trendPlatform,
                }
            }
        });
    }, [selectedProjectId, navigate]);

    const handleProjectChange = (e) => {
        const val = e.target.value;
        if (val) {
            setSearchParams({ project: val });
        } else {
            setSearchParams({});
        }
        setPlatform('meta');
        setCategoryFilter('all');
        setTrends([]);
        setSavedTrends([]);
    };

    // --- Derived ---
    const hasLinkedIn = projectData?.associatedLinkedInId || projects.find(p => p.id === selectedProjectId)?.hasLinkedIn;
    const filteredTrends = categoryFilter === 'all' ? trends : trends.filter(t => t.category === categoryFilter);

    // Saved trend lookup for quick "isSaved" checks
    const savedTitleSet = new Set(savedTrends.map(s => `${s.title}__${s.platform}`));
    const getSavedId = (title, plt) => {
        const match = savedTrends.find(s => s.title === title && s.platform === plt);
        return match?.id || null;
    };

    // --- Render ---
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-4xl font-black text-cream uppercase tracking-tight">
                    Trends <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream">Intelligence</span>
                </h1>
                <p className="text-sm text-cream/50 mt-2">Discover what's trending in your niche and create content from it.</p>
            </div>

            {/* Project Selector */}
            <div className="mb-8">
                <select
                    value={selectedProjectId || ''}
                    onChange={handleProjectChange}
                    disabled={projectsLoading}
                    className="w-full md:w-80 bg-black/20 border border-white/10 rounded-2xl text-cream py-3 px-5 text-sm font-medium appearance-none hover:bg-black/30 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue transition-colors cursor-pointer"
                >
                    <option value="">Select a project...</option>
                    {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {/* No Project State */}
            {!selectedProjectId && (
                <div className="text-center py-20 bg-warm-gray/10 rounded-3xl border border-white/5">
                    <FiTrendingUp className="h-12 w-12 text-cream/15 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-cream/60 mb-2">Select a project to explore trends</h3>
                    <p className="text-sm text-cream/30 max-w-md mx-auto">
                        Trends are personalized based on your project's industry, keywords, and target audience.
                        Select a project above to get started.
                    </p>
                </div>
            )}

            {/* Main Content */}
            {selectedProjectId && (
                <>
                    {/* Platform Tabs + Refresh */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                        <div className="flex p-1 bg-warm-gray/30 rounded-2xl border border-white/10">
                            <button
                                onClick={() => setPlatform('meta')}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
                                    platform === 'meta'
                                        ? 'bg-slate-blue/20 text-slate-blue shadow-sm'
                                        : 'text-cream/60 hover:text-cream'
                                }`}
                            >
                                Meta
                            </button>
                            {hasLinkedIn && (
                                <button
                                    onClick={() => setPlatform('linkedin')}
                                    className={`flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-bold transition-all ${
                                        platform === 'linkedin'
                                            ? 'bg-[#0A66C2]/20 text-[#0A66C2] shadow-sm'
                                            : 'text-cream/60 hover:text-cream'
                                    }`}
                                >
                                    <FaLinkedin className="h-3.5 w-3.5" /> LinkedIn
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            {cached && (
                                <span className="text-[10px] text-cream/20 font-bold uppercase tracking-wider">cached</span>
                            )}
                            <button
                                onClick={() => fetchTrends(true)}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-white/5 border border-white/10 text-cream/70 hover:text-cream hover:bg-white/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    {/* Category Filter Chips */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {CATEGORY_FILTERS.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setCategoryFilter(cat)}
                                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border ${
                                    categoryFilter === cat
                                        ? 'bg-slate-blue/20 text-slate-blue border-slate-blue/30'
                                        : 'text-cream/40 hover:text-cream/60 border-white/5 hover:border-white/15'
                                }`}
                            >
                                {cat === 'all' ? 'All' : CATEGORY_CONFIG[cat]?.label || cat}
                            </button>
                        ))}
                    </div>

                    {/* Trend Cards */}
                    {loading && trends.length === 0 ? (
                        <SkeletonCards />
                    ) : filteredTrends.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredTrends.map((trend, index) => (
                                <TrendCard
                                    key={`${trend.title}-${index}`}
                                    trend={trend}
                                    platform={platform}
                                    onSave={handleSaveTrend}
                                    onRemove={handleRemoveSavedTrend}
                                    isSaved={savedTitleSet.has(`${trend.title}__${platform}`)}
                                    savedId={getSavedId(trend.title, platform)}
                                    onCreateFrom={handleCreateFrom}
                                />
                            ))}
                        </div>
                    ) : !loading ? (
                        <div className="text-center py-16 bg-warm-gray/10 rounded-3xl border border-white/5">
                            <FiTrendingUp className="h-8 w-8 text-cream/15 mx-auto mb-3" />
                            <p className="text-sm text-cream/40">
                                {categoryFilter !== 'all'
                                    ? `No ${CATEGORY_CONFIG[categoryFilter]?.label?.toLowerCase() || categoryFilter} trends found. Try "All" filter.`
                                    : 'No trends available yet. Click Refresh to generate.'}
                            </p>
                        </div>
                    ) : null}

                    {/* Saved Trends Section */}
                    {savedTrends.length > 0 && (
                        <div className="mt-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-px flex-1 bg-white/5" />
                                <h3 className="text-xs font-black text-cream/30 uppercase tracking-widest flex items-center gap-2">
                                    <FiBookmark className="h-3.5 w-3.5" /> Saved Trends ({savedTrends.length})
                                </h3>
                                <div className="h-px flex-1 bg-white/5" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {savedTrends.map((saved) => (
                                    <div key={saved.id} className="p-4 bg-warm-gray/10 border border-white/5 rounded-2xl">
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <h4 className="text-sm font-bold text-cream leading-snug flex-1">{saved.title}</h4>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${(CATEGORY_CONFIG[saved.category] || CATEGORY_CONFIG.rising).className}`}>
                                                    {(CATEGORY_CONFIG[saved.category] || CATEGORY_CONFIG.rising).label}
                                                </span>
                                                <span className="text-[9px] font-bold text-cream/20 uppercase">{saved.platform}</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-cream/40 mb-3">{saved.description}</p>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleRemoveSavedTrend(saved.id)}
                                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-cream/30 hover:text-soft-coral hover:bg-soft-coral/10 transition-all"
                                            >
                                                <FiX className="h-3 w-3" /> Remove
                                            </button>
                                            <button
                                                onClick={() => handleCreateFrom(saved, saved.platform)}
                                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-cream/40 hover:text-slate-blue hover:bg-slate-blue/10 transition-all ml-auto"
                                            >
                                                Create <FiArrowRight className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
